class TextClamp extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-text-clamp-content]');
    this.toggle = this.querySelector('[data-text-clamp-toggle]');
    this.clampClass = this.dataset.lineClamp;

    if (!this.content || !this.toggle || !this.clampClass) return;

    this.expanded = false;
    this.animation = null;

    this.listeners = {
      click: this.toggleClamp.bind(this),
    };
    this.toggle.addEventListener('click', this.listeners.click);

    // Re-check overflow whenever the content is (re)laid out, e.g. on resize or when a parent collapsible opens
    this.resizeObserver = new ResizeObserver(() => this.updateToggleVisibility());
    this.resizeObserver.observe(this.content);
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.listeners.click);
    this.resizeObserver?.disconnect();
  }

  // Only show the toggle when the clamped text actually overflows
  updateToggleVisibility() {
    if (this.expanded || this.animation) return;
    this.toggle.hidden = this.content.scrollHeight <= this.content.clientHeight;
  }

  toggleClamp() {
    // Capture the current height before cancelling, so an interrupted animation continues from where it was
    const startHeight = this.content.clientHeight;
    this.animation?.cancel();

    if (this.expanded) {
      this.collapse(startHeight);
    }
    else {
      this.expand(startHeight);
    }
  }

  expand(startHeight) {
    this.expanded = true;
    this.toggle.setAttribute('aria-expanded', 'true');

    this.content.classList.remove(this.clampClass);
    this.animateHeight(startHeight, this.content.scrollHeight);
  }

  collapse(startHeight) {
    this.expanded = false;
    this.toggle.setAttribute('aria-expanded', 'false');

    // Measure the clamped height without showing the clamp yet, then re-apply it once the animation lands
    this.content.classList.add(this.clampClass);
    const endHeight = this.content.clientHeight;
    this.content.classList.remove(this.clampClass);

    this.animateHeight(startHeight, endHeight, () => {
      this.content.classList.add(this.clampClass);
    });
  }

  animateHeight(from, to, onFinish) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const animation = this.content.animate(
      { height: [`${from}px`, `${to}px`]},
      { duration: reducedMotion ? 0 : 350,
        easing: 'ease',
        fill: 'forwards' },
    );

    animation.finished
      .then(() => {
        if (onFinish) onFinish();
        // Release the pinned height once the final state is applied
        animation.cancel();
      })
      .catch(() => {}) // animation was cancelled by a new toggle
      .finally(() => {
        if (this.animation === animation) {
          this.animation = null;
          this.updateToggleVisibility();
        }
      });

    this.animation = animation;
  }
}

if (!customElements.get('text-clamp')) {
  customElements.define('text-clamp', TextClamp);
}
